import { useEffect, useRef, useState } from 'react';
import CreateInvoiceHeader from './createInvoiceHeader';
import { getCurrentDate } from '../../../../../utils/dateUtils';
import InvoiceForm from './invoiceForm';
import Button from '../../../../../component/ui/Button';
import useOfflineInvoice from '../components/useOfflineSaveInvoice';
import { createInvoice, fetchInvoiceNumber, updateInvoice } from '../../../../../services/invoices';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { hasPermission } from '../../../../../utils/controller';
import Modal from '../../../../../component/modal/Modal';
import InputField from '../../../../../component/form/FormInput';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import usePaymentMethod from '../../../../purchasebills/hooks/usePaymentMethod';
import { getRoundedOff, getRoundedOffAmount } from '../../../components/TaxesCalculation';
import { editInvoiceVerifyPassword } from '../../../../../services/settings/sales';
import AutoSaveModal from '../../../../components/autoSaveModal';

const CreateInvoice = () => {
	const navigate = useNavigate();
	const user = useSelector((state) => state?.userInfo?.userInfo?.data);
	const suburl = localStorage.getItem('suburl');
	const { paymentMethodList } = usePaymentMethod();
	const param = useParams();
	const id = param?.id;
	const { saveInvoice, getInvoice, isOnline, deleteInvoice, isSameAsOriginal } =
		useOfflineInvoice(id);
	const [loading, setLoading] = useState({
		isLoading: false,
		status: null,
		isSendEmail: false,
	});
	const [isNextLocation, setIsNextLocation] = useState(null);
	const [isLocationBlocker, setIsLocationBlocker] = useState(true);
	const [isInvoiceAutoSave, setIsInvoiceAutoSave] = useState(false);
	const [isSaveWithEmail, setIsSaveWithEmail] = useState(false);
	const [saveAsDraft, setSaveAsDraft] = useState(false);
	const [editInvoicePassword, setEditInvoicePassword] = useState('');
	const [errorPassword, setErrorPassword] = useState('');
	const [isAllowPasswordUpdateInvoice, setIsAllowPasswordUpdateInvoice] = useState(false);
	const [error, setError] = useState({});
	const [tableError, setTableError] = useState([]);
	const [isDraft, setIsDraft] = useState(false);
	const [showRestoreBanner, setShowRestoreBanner] = useState(false);
	const [isReadyToSave, setIsReadyToSave] = useState(false);
	const originalDataRef = useRef(null);
	const isInitialLoadRef = useRef(true);
	const wentOfflineRef = useRef(false);
	const hasUserInteractedRef = useRef(false);
	const [additionDiscountAndTax, setAdditionDiscountAndTax] = useState({
		discount: null,
		adj: null,
		round_off: null,
	});
	const [invoice, setInvoiceState] = useState({
		customer_id: null,
		doctor_id: null,
		account_id: null,
		invoice_number: '',
		date: null,
		sale_order_id: null,
		remarks: '',
		payment_method: null,
		due_date: null,
	});
	const [invoiceTable, setInvoiceTableState] = useState([]);
	const [oldInvoice, setOldInvoice] = useState({});
	const [oldInvoiceTable, setOldInvoiceTable] = useState([]);

	const setInvoice = (data) => {
		if (isReadyToSave) {
			// ✅ reads from state now
			hasUserInteractedRef.current = true;
		}
		setInvoiceState(data);
	};

	const setInvoiceTable = (data) => {
		if (isReadyToSave) {
			// ✅
			hasUserInteractedRef.current = true;
		}
		setInvoiceTableState(data);
	};

	function totalDiscountAmount() {
		let amount = invoiceTable?.reduce((sum, item) => Number(sum) + Number(item.amount), 0);
		return (amount * Number(additionDiscountAndTax.discount ?? 0)) / 100;
	}

	function totalTaxesAmount() {
		let totalTax1 = invoiceTable?.reduce(
			(sum, item) =>
				Number(sum) + (Number(item.amount ?? 0) * Number(item.product_tax1 ?? 0)) / 100,
			0,
		);
		let totalTax2 = invoiceTable?.reduce(
			(sum, item) =>
				Number(sum) + (Number(item.amount ?? 0) * Number(item.product_tax2 ?? 0)) / 100,
			0,
		);
		return Number(totalTax1) + Number(totalTax2);
	}

	function totalAmount() {
		let amount = invoiceTable?.reduce((sum, item) => Number(sum) + Number(item.amount), 0);
		let totalTaxes = totalTaxesAmount();

		let totalAmountWithDiscount =
			amount - (amount * Number(additionDiscountAndTax.discount ?? 0)) / 100;

		let totalAmountWithTax = Number(totalAmountWithDiscount) + Number(totalTaxes);

		return (
			Number(totalAmountWithTax) -
			Number(additionDiscountAndTax.adj ?? 0) -
			Number(additionDiscountAndTax.round_off ?? 0)
		);
	}

	const calculateAmount = () =>
		invoiceTable?.reduce((sum, item) => Number(sum) + Number(item.amount), 0);

	// Call this once API data is loaded in edit mode
	const setOriginalData = (apiInvoice, apiInvoiceTable, apiDiscountAndTax) => {
		originalDataRef.current = {
			...apiInvoice,
			invoiceTable: apiInvoiceTable,
			additionDiscountAndTax: apiDiscountAndTax,
		};
	};

	const onInvoiceLoaded = () => {
		setIsReadyToSave(true);
	};

	useEffect(() => {
		if (!id) {
			setIsReadyToSave(true); // ✅ create mode
		}
	}, []);

	const isCheckShowBanner = async () => {
		const saved = await getInvoice();
		if (saved?.customer_id || saved?.invoiceTable?.length > 0) {
			setShowRestoreBanner(true);
		}
	};

	useEffect(() => {
		isCheckShowBanner();
	}, []);

	useEffect(() => {
		if (isOnline) {
			if (wentOfflineRef.current) {
				wentOfflineRef.current = false;
				isCheckShowBanner();
			}
			return;
		}
		wentOfflineRef.current = true;
		if (!isReadyToSave) return; // ✅
		if (!hasUserInteractedRef.current) return;
		if (
			!isOnline &&
			invoice.invoice_number &&
			invoice.date &&
			(invoice.payment_method || invoice.customer_id || invoice.doctor_id || invoice.due_date)
		)
			setInvoiceOfflineData();
	}, [isOnline, invoice, invoiceTable, additionDiscountAndTax, isReadyToSave]);

	const isDataChanged = (invoice1, invoice2, table1, table2) =>
		JSON.stringify(invoice1) !== JSON.stringify(invoice2) ||
		JSON.stringify(table1) !== JSON.stringify(table2);

	const checkChangeInvoice = () => {
		if (!id || isDataChanged(oldInvoice, invoice, oldInvoiceTable, invoiceTable)) {
			setInvoiceOfflineData();
		}
	};

	const setInvoiceOfflineData = () => {
		if (id && !isReadyToSave) return;
		if (
			id &&
			isSameAsOriginal(
				{ ...invoice, invoiceTable, additionDiscountAndTax },
				originalDataRef.current,
			)
		)
			return;

		const timeout = setTimeout(() => {
			saveInvoice({
				...invoice,
				invoiceTable,
				additionDiscountAndTax,
				updatedAt: Date.now(),
			});
		}, 500);
		return () => clearTimeout(timeout);
	};

	const restoreAutoSaveData = async () => {
		const data = await getInvoice();
		if (!data) return;
		setInvoice(data);
		setInvoiceTable(data.invoiceTable);
		setAdditionDiscountAndTax(data.additionDiscountAndTax);
		deleteInvoice();
		onInvoiceLoaded();
		setShowRestoreBanner(false);
	};

	async function handleFetchInvoiceNumber() {
		try {
			const invoice = await fetchInvoiceNumber();
			setInvoiceState((prev) => ({
				...prev,
				invoice_number: invoice?.number,
				date: getCurrentDate(),
			}));
		} catch (error) {}
	}

	const checkValidation = (status) => {
		let newError = { ...error };
		let newTableError = [...tableError];
		if (!invoice.customer_id || invoice.customer_id === '' || invoice.customer_id === undefined) {
			newError.customer_id = 'Please select a valid customer from the list.';
		} else {
			delete newError.customer_id;
		}

		if (
			!invoice.invoice_number ||
			invoice.invoice_number === '' ||
			invoice.invoice_number === undefined
		) {
			newError.invoice_number = 'Please enter invoice number in the field';
		} else {
			delete newError.invoice_number;
		}
		if (status === 1) {
			if (
				!invoice.date ||
				invoice.date === '' ||
				invoice.date === undefined ||
				(invoice.date !== '' && newError.date)
			) {
				newError.date = newError.date ?? 'Please enter date in the field';
			} else {
				delete newError.date;
			}

			if (
				!invoice.payment_method ||
				invoice.payment_method === '' ||
				invoice.payment_method === undefined
			) {
				newError.payment_method = 'Please select a valid payment method from the list.';
			} else {
				delete newError.payment_method;
			}
		} else {
			delete newError.date;
			delete newError.payment_method;
		}
		if (status === 1) {
			// 🚨 First row validation
			const firstRow = invoiceTable[0];
			const isFirstRowEmpty =
				!firstRow?.product_id &&
				!firstRow?.batch_no &&
				(!firstRow?.quantity || Number(firstRow?.quantity) === 0);

			if (isFirstRowEmpty) {
				newTableError.push({ index: 0, name: 'product_id' });
				newTableError.push({ index: 0, name: 'batch_no' });
				newTableError.push({ index: 0, name: 'quantity' });
			} else {
				if (!firstRow.product_id) {
					newTableError.push({ index: 0, name: 'product_id' });
				}
				if (!firstRow?.batch_no) {
					newTableError.push({ index: 0, name: 'batch_no' });
				}
				if (!firstRow?.quantity || Number(firstRow?.quantity) === 0) {
					newTableError.push({ index: 0, name: 'quantity' });
				}
			}

			// Validate remaining rows
			invoiceTable.slice(1).forEach((row, index) => {
				const rowIndex = index + 1;

				const isRowEmpty =
					!row.product_id && !row.batch_no && (!row.quantity || Number(row.quantity) === 0);
				// Ignore completely empty rows
				if (isRowEmpty) {
					newTableError = newTableError.filter((err) => err.index !== rowIndex);
				} else {
					if (!row.product_id) {
						newTableError.push({ index: rowIndex, name: 'product_id' });
					}

					if (!row.batch_no) {
						newTableError.push({ index: rowIndex, name: 'batch_no' });
					}

					if (!row.quantity || Number(row.quantity) === 0) {
						newTableError.push({ index: rowIndex, name: 'quantity' });
					}
				}
			});
		} else {
			// Draft save → ignore table errors
			newTableError = [];
		}
		if (Object.keys(newError).length > 0 || newTableError.length > 0) {
			setError(newError);
			setTableError(newTableError);
			return { status: true, isTableError: Object.keys(newError).length > 0 ? false : true };
		} else {
			return { status: false };
		}
	};

	const verifyPasswordForUpdateInvoice = async () => {
		try {
			var body = {
				invoice_password: editInvoicePassword,
			};
			const res = await editInvoiceVerifyPassword(body);
			if (res.success) {
				if (res?.data?.invoice_edit_token) {
					handleSubmitInvoice(saveAsDraft ? 0 : 1, isSaveWithEmail, res?.data?.invoice_edit_token);
					setIsAllowPasswordUpdateInvoice(false);
				} else {
					toast.error('Password verification token is missing');
				}
			} else {
				toast.error(res.message);
			}
		} catch (error) {
			toast.error(error.message || 'Something went wrong!');
		}
	};

	const handleSubmitInvoice = async (status, isSendEmail = false, authToken = '') => {
		const isErrorShow = checkValidation(status);
		if (isErrorShow.status) {
			if (isErrorShow.isTableError) {
				toast.error('Error in Products List. Resolve the errors to continue.');
			} else if (status === 0) {
				toast.error('Please select a valid customer from the list.');
			} else {
				toast.error('Please fill all mandatory fields before saving');
			}
		} else {
			try {
				let invoice_id = null;
				setLoading((prev) => ({
					...prev,
					isLoading: true,
					status: status,
					isSendEmail: isSendEmail,
				}));

				const body = {
					customer_id: invoice.customer_id,
					doctor_id: invoice.doctor_id,
					account_id: invoice.account_id || null,
					invoice_number: invoice.invoice_number,
					date: invoice.date,
					sale_order_id: invoice.sale_order_id,
					remarks: invoice.remarks,
					payment_method_id: invoice.payment_method,
					due_date: invoice.due_date,
					status: status,
					discount: totalDiscountAmount(),
					adjustment: additionDiscountAndTax.adj ?? 0,
					taxes: totalTaxesAmount(),
					total_amount: getRoundedOffAmount(Number(totalAmount()).toFixed(2)),
					round_off: getRoundedOff(Number(totalAmount()).toFixed(2)),
					sub_total_amount: calculateAmount(),
					products:
						status === 0
							? invoiceTable.map((prod) => {
									const {
										quantity,
										mrp,
										expiry_date,
										batch_no,
										product_batch_id,
										amount,
										...rest
									} = prod;
									return {
										...rest,
										quantity: Number(quantity),
										mrp: Number(mrp),
										expiry: expiry_date,
										product_batch_id: Number(product_batch_id),
										amount: Number(amount),
									};
								})
							: invoiceTable
									.filter(
										(product) =>
											product.product_id !== '' &&
											product.product_id !== null &&
											product.quantity !== 0 &&
											product.quantity !== null &&
											product.quantity !== '' &&
											product.mrp !== 0 &&
											product.mrp !== '0.00' &&
											product.mrp !== null &&
											product.mrp !== '' &&
											product.amount !== 0 &&
											product.amount !== null &&
											product.amount !== '',
									)
									.map((prod) => {
										const {
											quantity,
											mrp,
											expiry_date,
											batch_no,
											product_batch_id,
											amount,
											...rest
										} = prod;
										return {
											...rest,
											quantity: Number(quantity),
											mrp: Number(mrp),
											expiry: expiry_date,
											product_batch_id: Number(product_batch_id),
											amount: Number(amount),
										};
									}),
				};

				if (id && !id.includes('id')) {
					if (navigator.onLine) {
						const res = await updateInvoice(id, body, authToken);
						if (res.success) {
							toast.success('Inovice updated Successfully');
							if (
								isSendEmail &&
								hasPermission('sales.invoices.communications_email', user?.role?.role_permissions)
							) {
								navigate(`/user/${suburl}/invoice-send-emails/${invoice_id}`);
							} else {
								navigate(`/user/${suburl}/invoices`);
							}
							deleteInvoice();
						} else {
							toast.error(res.message);
						}
					} else {
						saveInvoice(body);
						toast.success('Offline Invoice updated successfully.');
					}
				} else {
					if (navigator.onLine) {
						let res = await createInvoice(body);
						if (res.success) {
							invoice_id = res.data.invoice?.id;
							const modelStatus = !paymentMethodList.some(
								(item) =>
									item.key === invoice.payment_method &&
									(item.value === 'Credit' || item.value === 'Credit Note'),
							);
							toast.success('Invoice Created Successfully');
							if (isSendEmail) {
								setIsLocationBlocker(false);
								setIsInvoiceAutoSave(false);
								setTimeout(() => {
									navigate(`/user/${suburl}/invoice-send-emails/${invoice_id}`);
								}, 300);
							} else {
								setIsLocationBlocker(false);
								setIsInvoiceAutoSave(false);
								setTimeout(() => {
									navigate(`/user/${suburl}/invoices-details/${invoice_id}`, {
										state: { isOpen: status === 1 ? modelStatus : false },
									});
								}, 300);
							}
							deleteInvoice();
						} else {
							toast.error(res.message ?? 'something went wrong');
						}
					} else {
						saveInvoice(body);
						toast.success('Offline Invoice created successfully.');
					}
				}
			} catch (error) {
				toast.error(error.message ?? 'something went wrong');
			} finally {
				setLoading((prev) => ({
					...prev,
					isLoading: false,
					status: status,
					isSendEmail: isSendEmail,
				}));
			}
		}
	};

	useBlocker((tx) => {
		if (!id && isLocationBlocker) {
			setIsNextLocation(tx);
			setIsInvoiceAutoSave(true);
			return true;
		}
		return false;
	});

	useEffect(() => {
		if (!isReadyToSave) return; // ✅ now reactive
		if (isInitialLoadRef.current) {
			isInitialLoadRef.current = false;
			return;
		}
		if (
			invoice.payment_method ||
			invoice.customer_id ||
			invoice.doctor_id ||
			invoice.due_date ||
			(invoiceTable.length > 0 &&
				(invoiceTable[0].product_id !== '' ||
					invoiceTable[0].batch_no !== '' ||
					invoiceTable[0].quantity !== ''))
		) {
			checkChangeInvoice();
		}
	}, [
		invoice.payment_method,
		invoice.customer_id,
		invoice.doctor_id,
		invoice.due_date,
		invoice.remarks,
		invoiceTable,
		additionDiscountAndTax,
		isReadyToSave,
	]);

	return (
		<div>
			<CreateInvoiceHeader
				setIsInvoiceAutoSave={setIsInvoiceAutoSave}
				setIsAllowPasswordUpdateInvoice={setIsAllowPasswordUpdateInvoice}
				setIsSaveWithEmail={setIsSaveWithEmail}
				setSaveAsDraft={setSaveAsDraft}
				handleSubmitInvoice={handleSubmitInvoice}
				deleteInvoice={deleteInvoice}
				isDraft={isDraft}
				loading={loading}
			/>

			{/* ---------- Restore Banner ---------- */}
			{showRestoreBanner && (
				<div
					style={{
						backgroundColor: '#FFC008',
						padding: '5px 20px',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<div style={{ fontWeight: 600 }}>Offline data found. Do you want to restore it?</div>

					<div className="d-flex gap-3">
						<Button
							className="mx-1 btn-gray"
							button="Clear"
							onClick={() => {
								deleteInvoice();
								handleFetchInvoiceNumber();
								setShowRestoreBanner(false);
							}}
						/>

						<Button
							className="btn-primary"
							button="Restore"
							onClick={() => {
								restoreAutoSaveData();
							}}
						/>
					</div>
				</div>
			)}

			<InvoiceForm
				id={id}
				showRestoreBanner={showRestoreBanner}
				setInvoice={setInvoice}
				setOldInvoice={setOldInvoice}
				invoice={invoice}
				error={error}
				setError={setError}
				setIsDraft={setIsDraft}
				isDraft={isDraft}
				setInvoiceTable={setInvoiceTable}
				setOldInvoiceTable={setOldInvoiceTable}
				setAdditionDiscountAndTax={setAdditionDiscountAndTax}
				invoiceTable={invoiceTable}
				tableError={tableError}
				setTableError={setTableError}
				additionDiscountAndTax={additionDiscountAndTax}
				totalDiscountAmount={totalDiscountAmount}
				totalTaxesAmount={totalTaxesAmount}
				totalAmount={totalAmount}
				calculateAmount={calculateAmount}
				setInvoiceOfflineData={setInvoiceOfflineData}
				setOriginalData={setOriginalData}
				onInvoiceLoaded={onInvoiceLoaded}
			/>

			{isInvoiceAutoSave && (
				<AutoSaveModal
					isOpenModel={isInvoiceAutoSave}
					setIsOpenModel={setIsInvoiceAutoSave}
					user={user}
					permission={'sales.invoices.create'}
					type={'Invoice'}
					withoutSave={() => {
						setIsLocationBlocker(false);
						setIsInvoiceAutoSave(false);
						deleteInvoice();
						setTimeout(() => {
							navigate(isNextLocation.nextLocation?.pathname);
						}, 300);
					}}
					saveAsDraft={() => {
						setIsInvoiceAutoSave(false);
						handleSubmitInvoice(0);
					}}
					SaveAsContinue={() => {
						setIsInvoiceAutoSave(false);
						handleSubmitInvoice(1);
					}}
				/>
			)}
			{isAllowPasswordUpdateInvoice && (
				<Modal
					className="invoice-otp-modal"
					title={`Confirm Changes`}
					isOpen={isAllowPasswordUpdateInvoice}
					onClose={() => {
						setIsAllowPasswordUpdateInvoice(false);
					}}
					cancelButton={() => {
						setIsAllowPasswordUpdateInvoice(false);
					}}
				>
					<div
						style={{
							textAlign: 'center',
							backgroundColor: '#E6F5FF',
							padding: '20px 10px',
							marginBottom: '20px',
							borderRadius: '5px',
							fontWeight: '600',
							color: 'black',
						}}
					>
						For security reasons, please confirm your passeord to save changes to this invoice.
					</div>
					<div className="col-6 my-2">
						<InputField
							type="password"
							value={editInvoicePassword}
							placeholder="Password"
							label="Password"
							onChange={(value) => {
								setEditInvoicePassword(value);
								if (value) {
									setErrorPassword('');
								} else {
									setErrorPassword('Please enter password for update invoice');
								}
							}}
							error={errorPassword}
							required
							autoComplete="edit-invoice-password"
							showPasswordIcon
						/>
					</div>

					<div className="fs-5 fw-light mt-5 mb-3">
						This helps protect sensitive invoice updates{' '}
					</div>

					<div className="d-flex align-items-center justify-content-end gap-4 mt-4">
						<Button
							type="button"
							button={'Cancel'}
							className="mt-0 px-5 btn-secondary relative"
							onClick={() => setIsAllowPasswordUpdateInvoice(false)}
						/>
						<Button
							type="button"
							button={'Confirm & Save'}
							className="mt-0 px-5 btn-primary relative"
							onClick={() => {
								if (editInvoicePassword) {
									verifyPasswordForUpdateInvoice();
								} else {
									setErrorPassword('Please enter password for update invoice');
								}
							}}
						/>
					</div>
				</Modal>
			)}
		</div>
	);
};

export default CreateInvoice;
